/**
 * Immutable path write with structural sharing.
 *
 * `_.set` mutates; `_.cloneDeep` copies the whole document. This copies the
 * nodes ON the path and shares every node off it, which is what an editor
 * actually needs:
 *
 *   setIn(config, "components.3.config.text", "Tap!")
 *     → 4 shallow copies (root, components, [3], config)
 *     → config.components[4] === next.components[4]
 *
 * That second line is the point. Untouched branches keep their identity, so
 * `React.memo` and selector subscriptions can answer "did my slice change?"
 * with one `===` instead of a deep walk — impossible while every write
 * reallocates the tree.
 */

import _ from "lodash";

// Assigning these would reach the prototype chain rather than the object.
// `_.set` refuses them and so does this.
const FORBIDDEN_SEGMENTS = new Set(["__proto__", "constructor", "prototype"]);

/**
 * Normalise a lodash path to string segments.
 *
 * @param {string|Array} path e.g. "a.b[0].c" or ["a", "b", 0, "c"]
 * @returns {string[]}
 */
export function toSegments(path) {
  if (Array.isArray(path)) return path.map(String);
  if (typeof path !== "string" || path === "") return [];
  return _.toPath(path);
}

/** Dotted form of a path, so two spellings of the same path compare equal. */
export function toDotted(path) {
  return toSegments(path).join(".");
}

function isIndexSegment(segment) {
  return /^(0|[1-9]\d*)$/.test(segment);
}

/**
 * Shallow copy that preserves the container kind, creating one when the node
 * cannot hold a child — a missing branch becomes an array when the next segment
 * indexes into it, matching what `_.set` would have built.
 */
function shallowCopy(node, nextSegment) {
  if (Array.isArray(node)) return node.slice();
  if (_.isPlainObject(node)) return { ...node };
  return nextSegment !== undefined && isIndexSegment(nextSegment) ? [] : {};
}

function setInSegments(node, segments, index, value) {
  if (index >= segments.length) return value;

  const key = segments[index];
  if (FORBIDDEN_SEGMENTS.has(key)) return node;

  const next = shallowCopy(node, key);
  // Arrays index fine by string key, so no branch is needed here.
  next[key] = setInSegments(next[key], segments, index + 1, value);
  return next;
}

/**
 * Write `value` at `path`, returning a new root. `node` is never mutated.
 *
 * An empty path returns `value` — writing at the root replaces the document,
 * which is what a preset apply or an agent run does.
 *
 * @param {*} node
 * @param {string|Array} path
 * @param {*} value
 * @returns {*} the new root
 */
export function setIn(node, path, value) {
  return setInSegments(node, toSegments(path), 0, value);
}

/**
 * Read the value at `path`. Present so callers reading and writing the document
 * import one module and cannot disagree about how a path is spelled.
 *
 * @param {*} node
 * @param {string|Array} path
 * @param {*} [fallback]
 * @returns {*}
 */
export function getIn(node, path, fallback) {
  const segments = toSegments(path);
  if (segments.length === 0) return node === undefined ? fallback : node;
  return _.get(node, segments, fallback);
}

/**
 * True when `writtenPath` and `subscribedPath` overlap in either direction.
 *
 * A subscriber of `components` must hear about `components.3.config.text`
 * (an edit inside its slice), and a subscriber of `components.3.config.text`
 * must hear about a wholesale write of `components` (a preset or agent run
 * replacing everything under it).
 *
 * @param {string} subscribedPath dotted, "" for the whole document
 * @param {string} writtenPath dotted
 * @returns {boolean}
 */
export function pathsOverlap(subscribedPath, writtenPath) {
  if (subscribedPath === "" || writtenPath === "") return true;
  if (subscribedPath === writtenPath) return true;
  return (
    writtenPath.startsWith(`${subscribedPath}.`) ||
    subscribedPath.startsWith(`${writtenPath}.`)
  );
}
